import React, { createContext, useState, useContext, useMemo, useCallback } from "react";

/**
 * PreviewPanel Context
 *
 * Manages the preview panel state for product details, grids, and comparisons.
 * Content types: 'product' | 'grid' | 'compare' | null
 */
const PreviewPanelContext = createContext(null);

/**
 * Hook to access preview panel context
 */
export function usePreviewPanelContext() {
  const context = useContext(PreviewPanelContext);
  if (!context) {
    throw new Error(
      "usePreviewPanelContext must be used within a PreviewPanelProvider"
    );
  }
  return context;
}

/**
 * Hook with convenience methods for controlling the preview panel
 */
export function usePreviewPanel() {
  const { state, setState } = usePreviewPanelContext();

  const openProduct = useCallback((productId, productData = null) => {
    setState({
      isOpen: true,
      contentType: 'product',
      productId,
      productIds: null,
      productData,
    });
  }, [setState]);

  const openGrid = useCallback((products = [], title = null) => {
    setState({
      isOpen: true,
      contentType: 'grid',
      productId: null,
      productIds: null,
      products,
      title,
    });
  }, [setState]);

  const openCompare = useCallback((productIds = []) => {
    setState({
      isOpen: true,
      contentType: 'compare',
      productId: null,
      productIds,
    });
  }, [setState]);

  const close = useCallback(() => {
    setState({
      isOpen: false,
      contentType: null,
      productId: null,
      productIds: null,
    });
  }, [setState]);

  return {
    ...state,
    openProduct,
    openGrid,
    openCompare,
    close,
  };
}

/**
 * PreviewPanel Provider
 */
export default function PreviewPanelProvider({ children }) {
  const [state, setState] = useState({
    isOpen: false,
    contentType: null, // 'product' | 'grid' | 'compare'
    productId: null,
    productIds: null,
    products: [],
    productData: null,
    title: null,
  });

  const contextValue = useMemo(
    () => ({ state, setState }),
    [state]
  );

  return (
    <PreviewPanelContext.Provider value={contextValue}>
      {children}
    </PreviewPanelContext.Provider>
  );
}
